import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  alpha,
  useTheme,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { validateWorkExperience, validateProfessionalReferences, validateRevenue } from '../../../pages/designer-application/validations';

interface EditFormData {
  // User fields
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  businessName: string;
  businessWebsite?: string;
  businessAddress?: string;
  businessCity?: string;
  businessState?: string;
  businessZipCode?: string;
  // Application fields
  education: string[];
  experiences: Array<{
    id?: string;
    employmentType?: string;
    title?: string;
    company?: string;
    startDate?: string;
    endDate?: string;
    current?: boolean;
    firmSize?: string;
  }>;
  references: Array<{
    id?: string;
    firstName?: string;
    lastName?: string;
    title?: string;
    type?: string;
    businessName?: string;
    email?: string;
    phoneNumber?: string;
    startDate?: string;
    endDate?: string;
    yearsKnown?: number;
    ProjectType?: string;
  }>;
  businessGoals: {
    challenges?: string;
    expectations?: string;
    overview?: {
      numberOfProjectsCurrentYear?: number;
      numberOfProjectsLastYear?: number;
      totalRevenueCurrentYear?: string;
      totalRevenueLastYear?: string;
    };
    projectCapacity?: {
      largeScale?: string;
      smallScale?: string;
    };
  };
}

interface SectionEditFormProps {
  sectionId: string;
  editFormData: EditFormData;
  updateLoading: boolean;
  onFormChange: (field: string, value: string) => void;
  onFormChangeNested: (field: string, subField: string, value: any) => void;
  onFormChangeDeep: (field: string, subField: string, subSubField: string, value: any) => void;
  onAddEducation: () => void;
  onRemoveEducation: (index: number) => void;
  onUpdateEducation: (index: number, value: string) => void;
  onAddExperience: () => void;
  onRemoveExperience: (index: number) => void;
  onUpdateExperience: (index: number, field: string, value: any) => void;
  onAddReference: () => void;
  onRemoveReference: (index: number) => void;
  onUpdateReference: (index: number, field: string, value: any) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const SectionEditForm: React.FC<SectionEditFormProps> = ({
  sectionId,
  editFormData,
  updateLoading,
  onFormChange,
  onFormChangeNested,
  onFormChangeDeep,
  onAddEducation,
  onRemoveEducation,
  onUpdateEducation,
  onAddExperience,
  onRemoveExperience,
  onUpdateExperience,
  onAddReference,
  onRemoveReference,
  onUpdateReference,
  onSave,
  onCancel,
}) => {
  const theme = useTheme();

  const renderSectionContent = () => {
    switch (sectionId) {
      case 'basic':
        return (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="First Name"
                value={editFormData.firstName}
                onChange={(e) => onFormChange('firstName', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                value={editFormData.lastName}
                onChange={(e) => onFormChange('lastName', e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              value={editFormData.email}
              onChange={(e) => onFormChange('email', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Phone Number"
              value={editFormData.phoneNumber}
              onChange={(e) => onFormChange('phoneNumber', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Business Name"
              value={editFormData.businessName}
              onChange={(e) => onFormChange('businessName', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Business Website"
              value={editFormData.businessWebsite || ""}
              onChange={(e) => onFormChange('businessWebsite', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Business Address"
              value={editFormData.businessAddress || ""}
              onChange={(e) => onFormChange('businessAddress', e.target.value)}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Business City"
                value={editFormData.businessCity || ""}
                onChange={(e) => onFormChange('businessCity', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Business State"
                value={editFormData.businessState || ""}
                onChange={(e) => onFormChange('businessState', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Business Zip Code"
                value={editFormData.businessZipCode || ""}
                onChange={(e) => onFormChange('businessZipCode', e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>
          </Stack>
        );

      case 'education':
        return (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>
                Education Entries
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={onAddEducation}
              >
                Add Education
              </Button>
            </Stack>
            {editFormData.education.map((entry, index) => (
              <Stack key={index} direction="row" spacing={1} alignItems="center">
                <TextField
                  label={`Education ${index + 1}`}
                  value={entry}
                  onChange={(e) => onUpdateEducation(index, e.target.value)}
                  fullWidth
                  size="small"
                />
                <IconButton
                  onClick={() => onRemoveEducation(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        );

      case 'experience':
        // Convert EditFormData experiences to WorkExperience format for validation
        const workExperiences = editFormData.experiences.map(exp => ({
          id: exp.id,
          employmentType: exp.employmentType as any,
          title: exp.title,
          company: exp.company,
          startDate: exp.startDate,
          endDate: exp.endDate,
          current: exp.current,
          firmSize: exp.firmSize as any,
        }));
        const experienceValidation = validateWorkExperience(workExperiences);
        return (
          <Stack spacing={2}>
            {/* Validation Errors */}
            {!experienceValidation.isValid && (
              <Alert severity="error">
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Work Experience Requirements Not Met:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {experienceValidation.errors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>
                Work Experience
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={onAddExperience}
              >
                Add Experience
              </Button>
            </Stack>
            {editFormData.experiences.map((exp, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.02)
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Experience {index + 1}
                  </Typography>
                  <IconButton
                    onClick={() => onRemoveExperience(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Title"
                      value={exp.title || ""}
                      onChange={(e) => onUpdateExperience(index, 'title', e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Company"
                      value={exp.company || ""}
                      onChange={(e) => onUpdateExperience(index, 'company', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={exp.startDate || ""}
                      onChange={(e) => onUpdateExperience(index, 'startDate', e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={exp.endDate || ""}
                      onChange={(e) => onUpdateExperience(index, 'endDate', e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      disabled={exp.current}
                    />
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={exp.current || false}
                        onChange={(e) => onUpdateExperience(index, 'current', e.target.checked)}
                      />
                    }
                    label="Current Position"
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        );

      case 'references':
        // Convert EditFormData references to Reference format for validation
        const references = editFormData.references.map(ref => ({
          id: ref.id,
          firstName: ref.firstName,
          lastName: ref.lastName,
          title: ref.title,
          type: ref.type as any,
          businessName: ref.businessName,
          email: ref.email,
          phoneNumber: ref.phoneNumber,
          startDate: ref.startDate,
          endDate: ref.endDate,
          yearsKnown: ref.yearsKnown,
          ProjectType: ref.ProjectType as any,
        }));
        const referenceValidation = validateProfessionalReferences(references);
        return (
          <Stack spacing={2}>
            {/* Validation Errors */}
            {!referenceValidation.isValid && (
              <Alert severity="error">
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Reference Requirements Not Met:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {referenceValidation.errors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={600}>
                References
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={onAddReference}
              >
                Add Reference
              </Button>
            </Stack>
            {editFormData.references.map((ref, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.secondary.main, 0.02)
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Reference {index + 1}
                  </Typography>
                  <IconButton
                    onClick={() => onRemoveReference(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="First Name"
                      value={ref.firstName || ""}
                      onChange={(e) => onUpdateReference(index, 'firstName', e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Last Name"
                      value={ref.lastName || ""}
                      onChange={(e) => onUpdateReference(index, 'lastName', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Title"
                      value={ref.title || ""}
                      onChange={(e) => onUpdateReference(index, 'title', e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Business Name"
                      value={ref.businessName || ""}
                      onChange={(e) => onUpdateReference(index, 'businessName', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <TextField
                      label="Email"
                      type="email"
                      value={ref.email || ""}
                      onChange={(e) => onUpdateReference(index, 'email', e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Phone Number"
                      value={ref.phoneNumber || ""}
                      onChange={(e) => onUpdateReference(index, 'phoneNumber', e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>
                  <TextField
                    label="Years Known"
                    type="number"
                    value={ref.yearsKnown || ""}
                    onChange={(e) => onUpdateReference(index, 'yearsKnown', parseInt(e.target.value) || 0)}
                    fullWidth
                    size="small"
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        );

      case 'businessGoals':
        const businessGoalsValidation = validateRevenue({ businessGoals: editFormData.businessGoals });
        return (
          <Stack spacing={2}>
            {/* Validation Errors */}
            {!businessGoalsValidation.isValid && (
              <Alert severity="error">
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Revenue Requirements Not Met:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {businessGoalsValidation.errors.map((error, index) => (
                    <li key={index}>
                      <Typography variant="body2">{error}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <TextField
              label="Expectations"
              multiline
              rows={3}
              value={editFormData.businessGoals.expectations || ""}
              onChange={(e) => onFormChangeNested('businessGoals', 'expectations', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Challenges"
              multiline
              rows={3}
              value={editFormData.businessGoals.challenges || ""}
              onChange={(e) => onFormChangeNested('businessGoals', 'challenges', e.target.value)}
              fullWidth
              size="small"
            />
            <Typography variant="subtitle2" fontWeight={600}>
              Project Overview
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Projects Last Year"
                type="number"
                value={editFormData.businessGoals.overview?.numberOfProjectsLastYear || ""}
                onChange={(e) => onFormChangeDeep('businessGoals', 'overview', 'numberOfProjectsLastYear', parseInt(e.target.value) || undefined)}
                fullWidth
                size="small"
              />
              <TextField
                label="Projects This Year"
                type="number"
                value={editFormData.businessGoals.overview?.numberOfProjectsCurrentYear || ""}
                onChange={(e) => onFormChangeDeep('businessGoals', 'overview', 'numberOfProjectsCurrentYear', parseInt(e.target.value) || undefined)}
                fullWidth
                size="small"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Revenue Last Year"
                value={editFormData.businessGoals.overview?.totalRevenueLastYear || ""}
                onChange={(e) => onFormChangeDeep('businessGoals', 'overview', 'totalRevenueLastYear', e.target.value)}
                fullWidth
                size="small"
              />
              <TextField
                label="Revenue This Year"
                value={editFormData.businessGoals.overview?.totalRevenueCurrentYear || ""}
                onChange={(e) => onFormChangeDeep('businessGoals', 'overview', 'totalRevenueCurrentYear', e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Stack spacing={2}>
        {renderSectionContent()}
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={updateLoading}
            size="small"
            sx={{ flex: 1 }}
          >
            {updateLoading ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={updateLoading}
            size="small"
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
